import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Building2,
  Globe,
  Loader2,
  AlertCircle,
  CheckCircle2,
  CreditCard,
  User,
  Users,
  ChevronLeft,
  ChevronRight,
  Instagram,
  Smartphone,
  Monitor,
  Download,
  Receipt,
  Bell,
  Rocket
} from 'lucide-react';
import AddToHomescreenPrompt from '../components/AddToHomescreenPrompt';
import { MobilePageTitle } from '../components/MobilePageTitle';
import {
  urlsWhatsApp,
  MENSAJE_SOPORTE,
} from '../components/ContactarPorWhatsApp';
import { Icon } from '../design-system/icons';
import { useDeviceDetection } from '../hooks/useDeviceDetection';
import { supabase } from '../lib/supabase';
import { useSidebar } from '../context/SidebarContext';

// Import config section components
import { Schedules } from './config_sections/Schedules';
import { Locations } from './config_sections/Locations';
import { SalesAgent } from './config_sections/SalesAgent';
import { Equipo } from './config_sections/Equipo';
import { isPaidPlan } from '../lib/planCapabilities';
import { filtrarSecciones, seccionPermitida } from './config_sections/seccionesVisibles';
import { ONBOARDING_V2 } from '../components/onboarding/onboardingV2Routes';
import { LanguageAndRegion } from './config_sections/LanguageAndRegion';
import { Account } from './config_sections/Account';
import { InstagramIntegration } from './config_sections/InstagramIntegration';
import { Plan } from './config_sections/Plan';
import { MisMarcasSection } from './config_sections/MisMarcasSection';
import { Notificaciones } from './config_sections/Notificaciones';
import FuncionesAvanzadas from './config_sections/FuncionesAvanzadas';
import { paisFlags, FACTURACION_ENABLED } from '../lib/checkoutPlans';
import { esDuenoDeLaCuenta } from '../lib/duenoDeLaCuenta';

interface Profile {
  id: string;
  id_company: string | null;
  role: string | null;
  name?: string | null;
  email?: string | null;
  company_name?: string | null;
  company_logo?: string | null;
}

interface BillingConfig {
  cuit: string;
  nombre: string;
  condicion_iva: string;
  condicion_iibb: string;
  numero_iibb: string;
  fecha_inicio_actividades: string;
  provincia: string;
  localidad: string;
  domicilio: string;
  codigo_postal: string;
  config_lista: boolean;
}

// Helpers for billing cycle calculations (same logic as Plan.tsx)
function getCurrentMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

function getBillingCycleRange(paidPlanStartDate: string, upgradePlanDate?: string | null) {
  const now = new Date();
  const subscriptionDate = new Date(paidPlanStartDate);
  const referenceDate = upgradePlanDate ? new Date(upgradePlanDate) : subscriptionDate;
  const anchorDay = referenceDate.getDate();

  const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const anchorFor = (year: number, month: number) => {
    const dim = daysInMonth(year, month);
    const day = Math.min(anchorDay, dim);
    return new Date(year, month, day, 0, 0, 0, 0);
  };

  let start = anchorFor(now.getFullYear(), now.getMonth());
  if (start.getTime() > now.getTime()) {
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    start = anchorFor(prevMonth.getFullYear(), prevMonth.getMonth());
  }
  if (start.getTime() < subscriptionDate.getTime()) {
    start = new Date(
      subscriptionDate.getFullYear(),
      subscriptionDate.getMonth(),
      subscriptionDate.getDate(),
      0,
      0,
      0,
      0
    );
  }
  const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
  const end = anchorFor(nextMonth.getFullYear(), nextMonth.getMonth());
  return { start: start.toISOString(), end: end.toISOString() };
}

// Etiquetas de cada sección para el título mobile (cuando se está dentro de
// una sección, el título de la página es el nombre de esa sección; en el menú
// raíz es "Configuración"). Cubre todas las secciones, incluidas las que no
// aparecen en `configSections` (instagram, empleados, facturación, etc.).
const SETTINGS_SECTION_LABELS: Record<string, string> = {
  plan: 'Suscripción',
  companyData: 'Marcas',
  account: 'Información',
  language: 'Idioma y Región',
  notificaciones: 'Notificaciones',
  funcionesAvanzadas: 'Funciones avanzadas',
  facturacionPlan: 'Facturación de tu plan',
  atlasCelular: 'Atlas en tu Celular',
  atlasComputadora: 'Atlas en tu Computadora',
  installApp: 'Instalar aplicación',
  instagram: 'Instagram',
  company: 'Empleados',
  facturacion: 'Facturación',
  salesAgent: 'Agente de ventas',
  schedules: 'Horarios',
  locations: 'Ubicaciones',
};

export const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedTimezone, setSelectedTimezone] = useState<number>(-3);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('es');
  const [updatingTimezone, setUpdatingTimezone] = useState(false);
  // Entra por Cuenta — ADR-2026-08-04-configuracion-abre-en-cuenta. Era 'plan',
  // que es la pantalla a la que se llega desde el catálogo y desde la barra de
  // créditos; "Configuración" es lo que sos, no lo que pagás.
  const [activeSection, setActiveSection] = useState<string>('account');
  const [billingConfig, setBillingConfig] = useState<BillingConfig | null>(null);
  const [loadingBillingConfig, setLoadingBillingConfig] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const { setMobileBackOverride } = useSidebar();
  const deviceDetection = useDeviceDetection();

  // When inside a section on mobile, override Sidebar back to go to settings menu
  useEffect(() => {
    if (isMobile && !isMobileMenuOpen && activeSection !== '') {
      setMobileBackOverride(() => () => {
        setIsMobileMenuOpen(true);
      });
    } else {
      setMobileBackOverride(null);
    }
    return () => {
      setMobileBackOverride(null);
    };
  }, [isMobile, isMobileMenuOpen, activeSection, setMobileBackOverride]);

  // Plan summary state (for the compact quota block at the top)
  const [plan, setPlan] = useState<string>('');
  const [maxFotosMes, setMaxFotosMes] = useState<number>(0);
  const [usedFotos, setUsedFotos] = useState<number>(0);
  const [fotosGratisRestantes, setFotosGratisRestantes] = useState<number>(0);
  const [imagenesRestantes, setImagenesRestantes] = useState<number>(0);
  const [bonusImagenes, setBonusImagenes] = useState<number>(0);
  // País de la empresa → gatea la sección "Facturación de tu plan" (solo Argentina).
  const [isArgentina, setIsArgentina] = useState(false);
  // Gate extra de "Facturación de tu plan": oculta a usuarios reales hasta que AFIP esté en prod
  // (FACTURACION_ENABLED); los dev_testers la ven. Ver ADR-2026-08-13-facturacion-gate-dev-tester.
  const [devTester, setDevTester] = useState(false);
  // Dueño de la empresa (spec 2026-09-11-invitaciones-equipo §4.3): gatea Suscripción y Facturación.
  const [esDueno, setEsDueno] = useState(false);

  // Check if we're on mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Emit event to hide/show menu button based on whether a section is active in mobile
  useEffect(() => {
    if (isMobile) {
      const shouldHide = activeSection !== '' && !isMobileMenuOpen;
      window.dispatchEvent(new CustomEvent('hide-menu-button', {
        detail: { hide: shouldHide }
      }));
    }
    // Cleanup: show menu button when leaving Settings
    return () => {
      window.dispatchEvent(new CustomEvent('hide-menu-button', {
        detail: { hide: false }
      }));
    };
  }, [isMobile, activeSection, isMobileMenuOpen]);

  // Define config sections.
  // The "Plan" description swaps "fotos" → "créditos" for the
  // `enterprise_plus` plan so the language matches the rest of that
  // experience (sidebar bottom + Plan card content).
  const planUnit = plan === 'enterprise_plus' ? 'créditos' : 'fotos';
  // Orden: Cuenta → Plan → Marcas (ADR-2026-08-04-configuracion-abre-en-cuenta).
  // Antes abría con Plan. "Marcas" pierde el "Mis", como el resto de los ítems de
  // navegación de la app (ADR-2026-08-03-mi-marca-items-blancos-y-sin-mi).
  const configSections = [
    {
      // "Información" y no "Cuenta" (ADR-2026-08-04-la-seccion-de-cuenta-se-llama-informacion):
      // adentro están los datos de la persona —mail, nombre— y la sesión, y
      // "Cuenta" se confundía con el menú de cuenta del shell, que lleva acá.
      id: 'account',
      name: 'Información',
      description: 'Información de tu cuenta',
      icon: <User className="w-5 h-5" />
    },
    {
      id: 'plan',
      // "Suscripción" y no "Plan" — ADR-2026-08-04-la-seccion-se-llama-suscripcion:
      // es el nombre con el que el menú de cuenta del shell nombra lo mismo, y ese
      // menú es de donde se entra acá.
      name: 'Suscripción',
      description: `Tu suscripción y límite de ${planUnit}`,
      // El MISMO glifo que el ítem del menú de cuenta (`credit_card` de Material
      // Symbols) y no el `CreditCard` de lucide: son dos dibujos parecidos pero
      // distintos, y el usuario los ve a un click de distancia. Es el único ícono de
      // esta lista que no es de lucide; el que manda es el del menú, que es el que
      // se ve primero.
      icon: (
        <span className="material-symbols-rounded text-[20px] leading-none">
          credit_card
        </span>
      )
    },
    {
      id: 'companyData',
      name: 'Marcas',
      description: 'Identidad visual de tus marcas',
      icon: <Building2 className="w-5 h-5" />
    },
    {
      id: 'equipo',
      name: 'Equipo',
      description: 'Invitá a tu equipo a trabajar en tu cuenta',
      icon: <Users className="w-5 h-5" />
    },
    {
      id: 'language',
      name: 'Idioma y Región',
      description: 'Configuración de idioma y zona horaria',
      icon: <Globe className="w-5 h-5" />
    },
    {
      id: 'notificaciones',
      name: 'Notificaciones',
      description: 'Avisos para no perderte nada',
      icon: <Bell className="w-5 h-5" />
    },
    // Solo dev_tester por ahora (pedido de Mati, 2026-09-07): la sección se abre a todos
    // cuando Pre-lanzamientos y Verificación de calidad salgan de prueba (ver los dos filter de configSections).
    {
      id: 'funcionesAvanzadas',
      name: 'Funciones avanzadas',
      description: 'Pre-lanzamientos y otras funciones para catálogos grandes',
      icon: <Rocket className="w-5 h-5" />
    },
    {
      id: 'atlasCelular',
      name: 'Atlas en tu Celular',
      description: 'Accedé desde tu teléfono',
      icon: <Smartphone className="w-5 h-5" />,
      desktopOnly: true
    },
    {
      id: 'atlasComputadora',
      name: 'Atlas en tu Computadora',
      description: 'Accedé desde tu PC',
      icon: <Monitor className="w-5 h-5" />,
      mobileOnly: true
    },
    {
      id: 'installApp',
      name: 'Instalar aplicación',
      description: 'Accedé rápido como una app',
      icon: <Download className="w-5 h-5" />
    },
    {
      id: 'facturacionPlan',
      name: 'Facturación de tu plan',
      description: 'Tus facturas AFIP y datos fiscales',
      icon: <Receipt className="w-5 h-5" />,
      arOnly: true
    }
  ];

  /**
   * Lo que decide a qué secciones tiene ACCESO esta cuenta — el menú y el deep-link
   * preguntan lo mismo (ADR-2026-09-12-las-funciones-avanzadas-son-de-plan-pago).
   *
   * `planPago` sale del plan que esta pantalla ya tiene cargado; desconocido (`''`)
   * cuenta como gratis, que es la convención de estos gates.
   */
  const ctxDeAcceso = {
    isArgentina,
    devTester,
    esDueno,
    facturacionEnabled: FACTURACION_ENABLED,
    planPago: isPaidPlan(plan),
  };

  // Deep-link a una sección (`/configuracion?seccion=equipo`): lo usa la notificación de
  // "X se unió a tu equipo" (spec 2026-09-11-invitaciones-equipo §2.2).
  //
  // Pregunta por `seccionPermitida` y no por "existe en `configSections`", que es lo que
  // hacía: con la lista cruda, `?seccion=funcionesAvanzadas` abría la sección a cualquiera
  // aunque el menú no la listara — esconder el ítem no es bloquearlo
  // (ADR-2026-09-12-las-funciones-avanzadas-son-de-plan-pago).
  useEffect(() => {
    const seccion = new URLSearchParams(location.search).get('seccion');
    const destino = configSections.find((c) => c.id === seccion);
    if (destino && seccionPermitida(destino, ctxDeAcceso)) {
      setActiveSection(destino.id);
      setIsMobileMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleTimezoneChange = async (timezone: number) => {
    if (!profile?.id_company || updatingTimezone) return;

    setUpdatingTimezone(true);
    setError(null);
    setSuccess(null);

    try {
      const { error } = await supabase
        .from('config')
        .update({ timezone })
        .eq('id_company', profile.id_company);

      if (error) {
        console.error('Supabase timezone update error:', error);
        throw error;
      }

      setSelectedTimezone(timezone);
      setSuccess('Zona horaria actualizada correctamente');

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating timezone:', err);
      setError('Error al actualizar la zona horaria');
    } finally {
      setUpdatingTimezone(false);
    }
  };

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
    setSuccess('Idioma actualizado correctamente');
    setTimeout(() => setSuccess(null), 3000);
  };

  useEffect(() => {
    fetchProfile();

    // Refetch al cambiar de brand (el evento lo emite el Sidebar al switchear)
    const handleBrandChange = () => { fetchProfile(); };
    window.addEventListener('atlas:brand-changed', handleBrandChange);

    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    
    // Check if we're returning from Instagram OAuth
    const instagramConnected = urlParams.get('instagram_connected');
    
    if (instagramConnected === 'true') {
      // Clear the URL parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      // Show success message
      setSuccess('Cuenta de Instagram conectada correctamente');
      
      // Set Instagram as the active section
      setActiveSection('instagram');
      
      // Force a refresh of the Instagram connection data
      if (profile?.id_company) {
        setTimeout(() => {
          fetchProfile();
        }, 500);
      }
    }
    
    // Check if we're returning from MercadoPago checkout
    const preapprovalId = urlParams.get('preapproval_id');
    
    if (preapprovalId) {
      // Handle MercadoPago redirect
      handleMercadoPagoRedirect(preapprovalId);
      
      // Clear the URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // La sección pedida por `location.state` se atiende en su propio efecto, acá
    // abajo: este corre una sola vez y hacía falta que también escuchara los pedidos
    // posteriores al montaje.

    return () => {
      window.removeEventListener('atlas:brand-changed', handleBrandChange);
    };
  }, []);

  // Sección pedida por quien navega hasta acá (`navigate('/configuracion', { state:
  // { section } })`). Vivía adentro del efecto de montaje, que corre una sola vez, así
  // que quien YA estaba en Configuración y pedía una sección puntual no veía pasar
  // nada — la pastilla de créditos, que entra a Suscripción, es el caso
  // (ADR-2026-08-04-pastilla-creditos-lleva-a-suscripcion). `location.key` cambia en
  // cada navegación, incluso hacia la misma ruta, así que pedir dos veces la misma
  // sección vuelve a funcionar.
  useEffect(() => {
    const seccion = (location.state as { section?: string } | null)?.section;
    if (!seccion) return;
    setActiveSection(seccion);
    // El state se consume una sola vez: si queda en la entrada del historial, volver
    // acá con el botón de atrás reabriría esa sección en vez de la última que se vio.
    window.history.replaceState({}, document.title, location.pathname);
  }, [location.key]);

  // Set company as the active section when there's no company_id
  useEffect(() => {
    if (profile && !profile.id_company) {
      setActiveSection('company');
      setIsMobileMenuOpen(false);
    }
  }, [profile]);

  useEffect(() => {
    if (profile?.id_company && activeSection === 'facturacion') {
      fetchBillingConfig();
    }
  }, [profile?.id_company, activeSection]);

  // Fetch compact plan summary info for the top quota card
  useEffect(() => {
    const fetchPlanSummary = async () => {
      if (!profile?.id_company) return;
      try {
        const { data: company, error: companyError } = await supabase
          .from('company_profiles')
          .select('plan, max_fotos_mes, imagenes_restantes:imagenes_disponibles, paid_plan_start_date, upgrade_plan_date, pais, dev_tester, owner_user_id')
          .eq('id_company', profile.id_company)
          .single();
        if (companyError) throw companyError;

        setPlan(company.plan);
        setMaxFotosMes(company.max_fotos_mes || 0);
        setIsArgentina(paisFlags((company as { pais?: string | null }).pais).isArgentina);
        setDevTester((company as { dev_tester?: boolean | null }).dev_tester === true);
        // La fila entera: acá `company` ya existe (un `companyError` tira antes), pero la
        // firma pide la fila justamente para que no se pueda pasar un id "ausente".
        setEsDueno(esDuenoDeLaCuenta(company as { owner_user_id?: string | null }, profile.id));

        const remaining = typeof company.imagenes_restantes === 'number' ? company.imagenes_restantes : 0;

        if (company.plan === 'gratis') {
          setFotosGratisRestantes(remaining);
          setImagenesRestantes(remaining);
          setUsedFotos(0);
          setBonusImagenes(0);
        } else {
          setFotosGratisRestantes(0);
          setImagenesRestantes(remaining);
          if (company.max_fotos_mes && company.max_fotos_mes > 0) {
            if (remaining > company.max_fotos_mes) {
              setBonusImagenes(remaining - company.max_fotos_mes);
              setUsedFotos(0);
            } else {
              setBonusImagenes(0);
              const used = company.max_fotos_mes - remaining;
              setUsedFotos(used);
            }
          } else {
            setUsedFotos(0);
            setBonusImagenes(0);
          }
        }
      } catch (e) {
        // Non-blocking: just leave defaults
        console.warn('No se pudo obtener el resumen del plan:', e);
      }
    };

    fetchPlanSummary();
  }, [profile?.id_company]);

  // Handle MercadoPago redirect and save subscription information
  const handleMercadoPagoRedirect = async (preapprovalId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      
      // Get the user's profile to find their company_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
        
      if (profileError) throw profileError;
      if (!profile?.id_company) throw new Error('No company profile found');
      
      // Update the company_profiles table with the subscription information
      // We assume a subscription is active if we have a preapproval_id
      const { error: updateError } = await supabase
        .from('company_profiles')
        .update({
          preapproval_id: preapprovalId,
          status: 'active'  // Set a default status since MercadoPago doesn't provide one
        })
        .eq('id_company', profile.id_company);
      
      if (updateError) throw updateError;
      
      // Show success message and set plan as the active section
      setSuccess('¡Suscripción procesada correctamente!');
      setActiveSection('plan');
      
      // Force a refresh of profile data to get updated plan information
      fetchProfile();
    } catch (err) {
      console.error('Error handling MercadoPago redirect:', err);
      setError(err instanceof Error ? err.message : 'Error al procesar la suscripción');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, id_company, role, name, email, current_brand_id')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      if (!profileData) throw new Error('Profile not found');

      // Start with base profile
      let augmentedProfile: Profile = {
        id: profileData.id,
        id_company: profileData.id_company,
        role: profileData.role,
        name: profileData.name,
        email: profileData.email,
        company_name: null
      };
      // Backward compatibility: expose company_id alias expected by other sections
      (augmentedProfile as any).company_id = profileData.id_company;

      if (profileData.id_company) {
        // Fetch company info (para preapproval/status, no para nombre/logo)
        const { data: companyData } = await supabase
          .from('company_profiles')
          .select('nombre, logo_url, preapproval_id, status')
          .eq('id_company', profileData.id_company)
          .single();

        // Default: usar datos del company_profile como fallback legacy
        if (companyData) {
          augmentedProfile.company_name = companyData.nombre;
          augmentedProfile.company_logo = companyData.logo_url;
        }

        // Override con el active brand (mismo criterio que Sidebar):
        // 1. brand con id === current_brand_id
        // 2. brand con is_default = true
        // 3. primer brand
        const { data: brands } = await supabase
          .from('brand_profiles')
          .select('id, name, logo_url, is_default')
          .eq('id_company', profileData.id_company)
          .eq('is_draft', false);

        if (brands && brands.length > 0) {
          const currentBrandId = (profileData as any).current_brand_id as string | null;
          const activeBrand =
            brands.find((b) => b.id === currentBrandId) ||
            brands.find((b) => b.is_default) ||
            brands[0];
          if (activeBrand) {
            augmentedProfile.company_name = activeBrand.name;
            augmentedProfile.company_logo = activeBrand.logo_url;
          }
        }
      }

      setProfile(augmentedProfile);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err instanceof Error ? err.message : 'Error loading profile');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchBillingConfig = async () => {
    if (!profile?.id_company) return;
    
    try {
      setLoadingBillingConfig(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('config_facturacion')
        .select('*')
        .eq('id_company', profile.id_company)
        .single();
        
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      setBillingConfig(data || null);
    } catch (err) {
      console.error('Error fetching billing config:', err);
      setError(err instanceof Error ? err.message : 'Error loading billing configuration');
    } finally {
      setLoadingBillingConfig(false);
    }
  };
  
  const navigateToBilling = (editMode = false) => {
    navigate(editMode ? '/billing?edit=true' : '/billing');
  };

  const handleGoToPlanSection = () => {
    setActiveSection('plan');
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-8 pb-8 max-w-content mx-auto">
      {/* Título mobile: en el menú raíz es "Configuración"; dentro de una
          sección, el nombre de la sección (el back vuelve al menú). */}
      <MobilePageTitle
        accion={
          /* Soporte, sólo en el menú raíz de Configuración y no dentro de cada
             sección: acá es donde el usuario llega cuando algo no cierra —el
             plan, la facturación, los datos— y es el momento en que puede
             necesitar preguntar. Repetirlo en las nueve secciones sería el mismo
             botón nueve veces.

             Link directo a `wa.me`: `MobilePageTitle` es `sm:hidden`, así que
             esta fila sólo existe en teléfonos y el modal del QR —que sirve para
             escanear DESDE otro aparato— no tendría sentido. En desktop el acceso
             sigue siendo el control "Whatsapp" del encabezado del shell. */
          isMobileMenuOpen ? (
            <a
              href={urlsWhatsApp({ mensaje: MENSAJE_SOPORTE }).directa}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-black/[0.07] bg-white px-3 text-[13.5px] font-medium text-brand-black/80 transition-opacity active:opacity-70"
            >
              <Icon name="solid/whatsapp" size={16} className="shrink-0" />
              Whatsapp
            </a>
          ) : undefined
        }
      >
        {isMobileMenuOpen
          ? 'Configuración'
          : SETTINGS_SECTION_LABELS[activeSection] || 'Configuración'}
      </MobilePageTitle>
      {/* Desktop header */}
      <div className="hidden sm:flex items-center justify-between mb-5 h-11">
        <h1 className="text-[17px] font-semibold text-brand-black/90 mt-4">Configuración</h1>
      </div>

      {/* Error and Success Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2.5 text-[15px] text-green-700">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          <p>{success}</p>
        </div>
      )}

      

      {/* Config Sections Navigation - Only shown as menu in mobile or sidebar in desktop */}
      <div className="flex flex-col md:flex-row gap-6 lg:gap-8">
        {/* Mobile version - Show only if menu is open */}
        {isMobile ? (
          isMobileMenuOpen ? (
            <div className="w-full">
              <div className="bg-surface-card rounded-lg border border-neutral-200 p-4">
                {/* La cabecera de identidad se fue también acá, por el mismo motivo
                    que en desktop (ADR-2026-08-04-configuracion-sin-tarjeta-de-identidad):
                    si se saca de un lado y se deja del otro, la pantalla vuelve a ser
                    dos pantallas distintas según el tamaño. */}
                {/* Plan summary */}
                {profile?.id_company && (
                  <div className="mb-4 border border-neutral-200 rounded-md p-3">
                    <div className="mb-2 text-[13px] text-neutral-600">
                      {plan === 'gratis' ? (
                        <span>Te quedan <span className="font-semibold text-brand-black/90">{fotosGratisRestantes}</span> fotos para generar</span>
                      ) : (
                        <span>
                          Te quedan{' '}
                          <span className="font-semibold text-brand-black/90">{imagenesRestantes}</span>{' '}
                          imágenes
                        </span>
                      )}
                    </div>
                    <div className="w-full bg-neutral-200 rounded-full h-[5px] mb-3 overflow-hidden">
                      {plan === 'gratis' ? (
                        <div
                          className="bg-brand-primary h-[5px] rounded-full"
                          style={{ width: `${Math.min(maxFotosMes > 0 ? (fotosGratisRestantes / maxFotosMes) * 100 : (fotosGratisRestantes > 0 ? 100 : 0), 100)}%` }}
                        />
                      ) : (
                        <div className="flex h-[5px]">
                          <div
                            className="bg-brand-primary h-[5px]"
                            style={{ 
                              width: `${Math.min(maxFotosMes > 0 ? (Math.min(imagenesRestantes, maxFotosMes) / maxFotosMes) * 100 : 0, 100)}%`,
                              borderRadius: bonusImagenes > 0 ? '9999px 0 0 9999px' : '9999px'
                            }}
                          />
                          {bonusImagenes > 0 && (
                            <div
                              className="bg-green-500 h-[5px] rounded-r-full"
                              style={{ 
                                width: `${Math.min((bonusImagenes / maxFotosMes) * 100, 50)}%`
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                    {esDueno && (
                      <button
                        onClick={handleGoToPlanSection}
                        className="w-full h-10 bg-surface-card hover:bg-neutral-50 border border-neutral-200 text-[15px] font-medium text-brand-black/90 rounded-md shadow-sm transition-colors"
                      >
                        Gestionar plan
                      </button>
                    )}
                  </div>
                )}

              <div className="space-y-0.5">
                  {profile?.id_company 
                    ? filtrarSecciones(configSections, { vista: 'mobile', ...ctxDeAcceso }).map(section => (
                        <button
                          key={section.id}
                          onClick={() => {
                            if (section.id === 'facturacionPlan') { navigate('/facturacion'); return; }
                            setActiveSection(section.id);
                            setIsMobileMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 p-2.5 rounded-md text-left transition-colors hover:bg-surface-elevated"
                        >
                          <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-neutral-100 text-neutral-600">
                            {section.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[15px] font-medium text-brand-black/90">{section.name}</div>
                            <div className="text-[13px] text-neutral-500">{section.description}</div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />
                        </button>
                      ))
                    : (
                        <button
                          key="account"
                          onClick={() => {
                            setActiveSection('account');
                            setIsMobileMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 p-2.5 rounded-md text-left transition-colors hover:bg-surface-elevated"
                        >
                          <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-neutral-100 text-neutral-600">
                            <User className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-[15px] font-medium text-brand-black/90">Cuenta</div>
                            <div className="text-[13px] text-neutral-500">Información de tu cuenta</div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />
                        </button>
                      )
                  }
                </div>
              </div>
            </div>
          ) : null
        ) : (
          /* Desktop: el menú va SUELTO sobre el fondo gris de la página —sin card
             propia— y el contenido de la sección elegida en el recuadro blanco de
             la derecha (ADR-2026-07-31-configuracion-menu-sobre-gris-sola). Es la misma división de la referencia: la
             navegación es cromo de la pantalla, no contenido.

             Las filas quedan a una línea (ícono + nombre): la descripción y el
             chevron que tenían servían en mobile, donde la lista ES la pantalla;
             acá, con el contenido al lado y sin card que las contenga, ensuciaban
             el margen. En mobile siguen tal cual. */
          // Con el catálogo de planes abierto el menú se va: los tres planes
          // necesitan el ancho entero (ADR-2026-07-31-embebido-deja-ser-modal-scrim). Se oculta con `hidden` en vez de
          // desmontarlo para no perder el scroll ni remontar la cabecera al volver.
          <div
            // 440px desde `lg`, que es el ancho de la columna izquierda de `Mi marca`
            // (ADR-2026-08-04-configuracion-copia-el-menu-de-mi-marca) — con la misma
            // columna, el área de la derecha mide lo mismo en las dos pantallas.
            // Entre `md` y `lg` se queda en 236: ahí `Mi marca` todavía es de una sola
            // columna, y 440 sobre 768px de ancho dejaría la sección en ~300px.
            className="w-full md:w-[236px] md:shrink-0 lg:w-[440px]"
          >
            <div className="md:sticky md:top-4">
              {/* Acá vivía la cabecera con el logo, el nombre y la marca —
                  ADR-2026-08-04-configuracion-sin-tarjeta-de-identidad. Repetía lo que
                  ya dice el menú de cuenta del shell (que es de donde se entra) y
                  mezclaba dos identidades en un bloque: el nombre de la PERSONA con el
                  logo y el nombre de la MARCA. Las dos cosas tienen su sección —Cuenta
                  y Marcas— y ahora son las dos primeras de la lista. */}
              {/* Mismo tratamiento que los tabs de `Mi marca`: tarjeta blanca con
                  borde, el seleccionado en `#EBEFFE` con el texto azul —el mismo par
                  que usa un ítem de la sidebar— y los 8px de `gap-2` entre uno y otro
                  (ADR-2026-08-04-configuracion-copia-el-menu-de-mi-marca). Antes eran
                  filas sueltas sobre el gris, más chicas, sin caja y separadas por 2px.
                  Los íconos se conservan: `Mi marca` no los tiene porque son tres tabs,
                  y acá son seis secciones donde el ícono es la mitad de la lectura. */}
              <div className="flex flex-col gap-2">
                {profile?.id_company
                  ? filtrarSecciones(configSections, { vista: 'desktop', ...ctxDeAcceso }).map(section => {
                      const isActive = activeSection === section.id;
                      return (
                        <button
                          key={section.id}
                          // `facturacionPlan` es una PANTALLA aparte, no una sección de
                          // este menú: navega en vez de cambiar `activeSection`. Lo trajo
                          // `principal` con la facturación argentina; el estilo de la
                          // tarjeta es el del rediseño
                          // (ADR-2026-08-04-configuracion-copia-el-menu-de-mi-marca).
                          onClick={() => section.id === 'facturacionPlan' ? navigate('/facturacion') : setActiveSection(section.id)}
                          className={`w-full flex items-center gap-2.5 px-4 py-3 rounded-xl border text-left text-[14px] font-medium transition-colors ${
                            isActive
                              ? 'border-transparent bg-[#EBEFFE] text-brand-primary'
                              : 'border-black/[0.07] bg-white hover:bg-black/[0.02]'
                          }`}
                        >
                          {/* `inline-flex items-center` y no un `<span>` pelado —
                              ADR-2026-08-04-el-glifo-necesita-caja-flex. El ícono de
                              Suscripción es un GLIFO, no un `<svg>`: como texto se
                              apoya en la línea base, y con el descendente de la fuente
                              el envoltorio quedaba más alto que el resto de la fila.
                              Medido: la fila crecía 4px y el ícono quedaba 2,5px por
                              encima del centro de la etiqueta. Con la caja flex el
                              hijo se centra y los cinco íconos de lucide no se mueven
                              ni un píxel. */}
                          <span
                            className={`inline-flex shrink-0 items-center ${isActive ? 'text-brand-primary' : 'text-neutral-500'}`}
                          >
                            {section.icon}
                          </span>
                          <span className="flex-1 truncate">{section.name}</span>
                          <ChevronRight
                            className={`w-4 h-4 shrink-0 transition-opacity ${isActive ? 'opacity-100' : 'opacity-0'}`}
                          />
                        </button>
                      );
                    })
                  : (
                      <button
                        key="account"
                        onClick={() => setActiveSection('account')}
                        className="w-full flex items-center gap-2.5 px-4 py-3 rounded-xl border border-transparent bg-[#EBEFFE] text-left text-[14px] font-medium text-brand-primary transition-colors"
                      >
                        <span className="shrink-0 text-brand-primary">
                          <User className="w-5 h-5" />
                        </span>
                        <span className="flex-1 truncate">Cuenta</span>
                        <ChevronRight className="w-4 h-4 shrink-0" />
                      </button>
                    )
                }
              </div>
            </div>
          </div>
        )}

        {/* Content Area - On mobile, only shown when menu is closed and section is selected */}
        {/* En desktop, ESTE es el recuadro blanco: las secciones de adentro ya no
            traen el suyo (ADR-2026-07-31-configuracion-menu-sobre-gris-sola), así no quedan cards anidadas. En mobile no hay
            recuadro —la sección ocupa la pantalla— y cada una se ve como antes. */}
        {(!isMobile || (isMobile && !isMobileMenuOpen)) && (
          <div className="w-full md:flex-1 md:min-w-0 md:bg-surface-card md:rounded-2xl md:border md:border-black/[0.07] md:p-6">
            {profile?.id_company ? (
              <>
                {activeSection === 'plan' && (
                  <Plan
                    profile={profile}
                    setError={setError}
                    setSuccess={setSuccess}
                  />
                )}

                {activeSection === 'language' && (
                  <LanguageAndRegion
                    selectedTimezone={selectedTimezone}
                    selectedLanguage={selectedLanguage}
                    updatingTimezone={updatingTimezone}
                    onTimezoneChange={handleTimezoneChange}
                    onLanguageChange={handleLanguageChange}
                  />
                )}

                {activeSection === 'notificaciones' && (
                  <Notificaciones
                    profile={profile}
                    devTester={devTester}
                    setError={setError}
                    setSuccess={setSuccess}
                  />
                )}

                {activeSection === 'funcionesAvanzadas' && (
                  <FuncionesAvanzadas
                    profile={profile}
                    setError={setError}
                    setSuccess={setSuccess}
                  />
                )}
                
                {activeSection === 'schedules' && (
                  <Schedules 
                    profile={profile} 
                    setError={setError} 
                    setSuccess={setSuccess} 
                  />
                )}
                
                {activeSection === 'locations' && (
                  <Locations 
                    profile={profile} 
                    setError={setError} 
                    setSuccess={setSuccess} 
                  />
                )}
                
                {activeSection === 'salesAgent' && (
                  <SalesAgent 
                    profile={profile} 
                    setError={setError} 
                    setSuccess={setSuccess} 
                  />
                )}
                
                {activeSection === 'instagram' && (
                  <InstagramIntegration
                    profile={profile}
                    setError={setError}
                    setSuccess={setSuccess}
                  />
                )}
                
                {activeSection === 'companyData' && (
                  <MisMarcasSection profile={profile} />
                )}
                
                {activeSection === 'equipo' && (
                  <Equipo plan={plan} miId={profile.id} setError={setError} setSuccess={setSuccess} />
                )}
                
                {activeSection === 'facturacion' && (
                  <div>
                    <h2 className="text-[17px] font-semibold text-brand-black/90 mb-4">Facturación Electrónica</h2>
                    
                    {loadingBillingConfig ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
                      </div>
                    ) : billingConfig && billingConfig.config_lista ? (
                      <div>
                        <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-5">
                          <p className="text-[13px] text-green-700">
                            La configuración de facturación está completa y lista para usar.
                          </p>
                        </div>
                        
                        <div className="space-y-3 mb-5">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <p className="text-[13px] font-medium text-neutral-500">CUIT</p>
                              <p className="text-[15px] text-brand-black/90">{billingConfig.cuit}</p>
                            </div>
                            <div>
                              <p className="text-[13px] font-medium text-neutral-500">Razón Social</p>
                              <p className="text-[15px] text-brand-black/90">{billingConfig.nombre}</p>
                            </div>
                            <div>
                              <p className="text-[13px] font-medium text-neutral-500">Condición IVA</p>
                              <p className="text-[15px] text-brand-black/90">{billingConfig.condicion_iva}</p>
                            </div>
                            <div>
                              <p className="text-[13px] font-medium text-neutral-500">Condición IIBB</p>
                              <p className="text-[15px] text-brand-black/90">{billingConfig.condicion_iibb}</p>
                            </div>
                            <div>
                              <p className="text-[13px] font-medium text-neutral-500">Número IIBB</p>
                              <p className="text-[15px] text-brand-black/90">{billingConfig.numero_iibb}</p>
                            </div>
                            <div>
                              <p className="text-[13px] font-medium text-neutral-500">Fecha Inicio Actividades</p>
                              <p className="text-[15px] text-brand-black/90">{new Date(billingConfig.fecha_inicio_actividades).toLocaleDateString()}</p>
                            </div>
                            <div>
                              <p className="text-[13px] font-medium text-neutral-500">Domicilio</p>
                              <p className="text-[15px] text-brand-black/90">{billingConfig.domicilio}</p>
                            </div>
                            <div>
                              <p className="text-[13px] font-medium text-neutral-500">Localidad</p>
                              <p className="text-[15px] text-brand-black/90">{billingConfig.localidad}, {billingConfig.provincia} ({billingConfig.codigo_postal})</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex justify-end">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              navigateToBilling(true);
                            }}
                            className="h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                          >
                            Volver a configurar facturación
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <CreditCard className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                        <h3 className="text-[15px] font-medium text-brand-black/90 mb-1">
                          Configuración de facturación no completada
                        </h3>
                        <p className="text-[13px] text-neutral-500 mb-5 max-w-md mx-auto">
                          Configure los datos necesarios para la facturación electrónica de su empresa
                        </p>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            navigateToBilling(false);
                          }}
                          className="h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                        >
                          Configurar facturación
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                {activeSection === 'account' && (
                  <Account
                    setError={setError}
                    setSuccess={setSuccess}
                  />
                )}

                {activeSection === 'atlasCelular' && (
                  <div>
                    <h2 className="text-[17px] font-semibold text-brand-black/90 mb-1">Atlas en tu Celular</h2>
                    <p className="text-[15px] text-neutral-500 mb-5">
                      Escaneá el código QR con la cámara de tu celular para acceder a Atlas desde tu teléfono. 
                      Podrás crear imágenes y videos desde cualquier lugar.
                    </p>
                    <div className="flex flex-col items-center">
                      <div className="p-4 rounded-md border border-neutral-200">
                        <img 
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent('https://app.estudio-atlas.com')}`}
                          alt="QR Code para Atlas"
                          className="w-48 h-48 rounded-sm"
                        />
                      </div>
                      <p className="text-[13px] text-neutral-500 mt-3 text-center">
                        Apuntá la cámara de tu celular al código QR
                      </p>
                    </div>
                  </div>
                )}

                {activeSection === 'atlasComputadora' && (
                  <div>
                    <h2 className="text-[17px] font-semibold text-brand-black/90 mb-1">Atlas en tu Computadora</h2>
                    <p className="text-[15px] text-neutral-500 mb-5">
                      Accedé a Atlas desde tu computadora para una experiencia más completa. 
                      Podés usar todas las funciones de Atlas en una pantalla más grande.
                    </p>
                    <div className="bg-surface-elevated border border-neutral-200 rounded-md p-5 text-center">
                      <Monitor className="w-10 h-10 text-neutral-400 mx-auto mb-3" />
                      <p className="text-[15px] font-medium text-brand-black/90 mb-1">
                        Ingresá a Atlas desde tu navegador
                      </p>
                      <p className="text-[17px] font-semibold text-brand-primary mb-3">
                        app.estudio-atlas.com
                      </p>
                      <p className="text-[13px] text-neutral-500">
                        Iniciá sesión con tu cuenta para acceder a todas tus imágenes y configuraciones.
                      </p>
                    </div>
                  </div>
                )}

                {activeSection === 'installApp' && (
                  <>
                    {deviceDetection.isStandalone ? (
                      <div>
                        <h2 className="text-[17px] font-semibold text-brand-black/90 mb-1">Instalar aplicación</h2>
                        <div className="mt-4 flex flex-col items-center text-center py-6">
                          <div className="w-14 h-14 rounded-md bg-green-50 border border-green-200 flex items-center justify-center mb-4">
                            <CheckCircle2 className="w-7 h-7 text-green-600" />
                          </div>
                          <p className="text-[15px] font-medium text-brand-black/90 mb-1">
                            Atlas ya está instalada
                          </p>
                          <p className="text-[13px] text-neutral-500 max-w-xs">
                            Estás usando Atlas como aplicación. Podés acceder desde tu pantalla de inicio.
                          </p>
                        </div>
                      </div>
                    ) : deviceDetection.canInstall ? (
                      <div>
                        <h2 className="text-[17px] font-semibold text-brand-black/90 mb-1">Instalar aplicación</h2>
                        <p className="text-[15px] text-neutral-500 mb-5">
                          Instalá Atlas como aplicación para acceder de forma rápida desde tu {deviceDetection.device === 'desktop' ? 'escritorio' : 'pantalla de inicio'}.
                        </p>

                        <button
                          onClick={() => setShowInstallPrompt(true)}
                          className="w-full h-11 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors flex items-center justify-center gap-2"
                        >
                          <Download className="w-4 h-4" />
                          {/* En Chrome/Edge desktop el prompt abre el diálogo
                              nativo de una; en el resto son instrucciones. */}
                          {deviceDetection.installMethod === 'native-prompt' &&
                          deviceDetection.hasNativePrompt
                            ? 'Instalar Atlas'
                            : 'Ver instrucciones de instalación'}
                        </button>

                        <div className="mt-5 px-3 py-2.5 bg-brand-primary/[0.04] rounded-md border border-brand-primary/10">
                          <p className="text-[13px] text-brand-black/50 leading-relaxed">
                            Al instalar, se creará un acceso directo que abre Atlas como una app independiente, sin la barra del navegador.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <h2 className="text-[17px] font-semibold text-brand-black/90 mb-1">Instalar aplicación</h2>
                        <div className="mt-4 flex flex-col items-center text-center py-6">
                          <div className="w-14 h-14 rounded-md bg-neutral-100 flex items-center justify-center mb-4">
                            <Download className="w-7 h-7 text-neutral-400" />
                          </div>
                          <p className="text-[15px] font-medium text-brand-black/90 mb-1">
                            Navegador no compatible
                          </p>
                          <p className="text-[13px] text-neutral-500 max-w-xs">
                            Tu navegador actual no soporta la instalación como app. Probá abrir Atlas en Safari o Chrome.
                          </p>
                        </div>
                      </div>
                    )}

                    {showInstallPrompt && (
                      <AddToHomescreenPrompt
                        appName="Atlas"
                        appIconUrl="/favicon.png"
                        delayMs={0}
                        forceShow
                        origen="ajustes"
                        onDismiss={() => setShowInstallPrompt(false)}
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              // Sin empresa: en la práctica nadie llega acá (checkUserProfile redirige al cuestionario
              // antes); el fallback sólo ofrece ir a configurar la cuenta.
              <div className="text-center py-10">
                <p className="text-[15px] text-brand-black/90">Tu cuenta todavía no tiene una empresa.</p>
                <button
                  type="button"
                  onClick={() => navigate(ONBOARDING_V2.setup)}
                  className="mt-4 py-2 px-4 rounded-md bg-brand-primary text-white text-[14px] font-medium"
                >
                  Configurar mi cuenta
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
